import { createGlobalState } from '@vueuse/core';
import { ref, nextTick, type Ref } from 'vue';
import { useToasted } from '~/composables/useToasted';
import { HTTP_INTERNAL_SERVER_ERROR, HTTP_NOT_FOUND } from '~/support/HttpStatuses';
import { adminTeamsClient, adminUsersClient, groupsClient, groupUsersClient, groupInvitationsClient } from '~/services';
import type { Group } from '~/services/groups/groupsClient.types';
import type { KioskTeam } from '~/services/adminTeamsClient.types';
import type { User } from '~/services/usersClient.types';
import type { OrgUnit } from '~/types/orgUnit.types';

export const useGroupDetails = createGlobalState(() => {
    const toasted = useToasted();

    const group: Ref<Partial<Group>> = ref({});
    const loading = ref(true);
    const contactGroupsDisabled = ref(false);

    const catchRequestError = (error: unknown) => {
        console.error(error);
        const response = (error as { response?: { status?: number; data?: { message?: string } } })?.response;
        const status = response?.status;
        const message = response?.data?.message;
        if (!status || status >= HTTP_INTERNAL_SERVER_ERROR || status == HTTP_NOT_FOUND) {
            toasted?.global.platform_error();
        } else {
            toasted?.error(message || 'Something went wrong.');
        }
    };

    let onMountedGroupCallback: () => void = () => {};
    const onMountedGroup = (callback: () => void) => {
        onMountedGroupCallback = callback;
    };

    const mount = async (teamId?: number, groupId?: string) => {
        await nextTick();

        team.value = undefined;
        teamOwner.value = undefined;
        groupOwner.value = undefined;
        invitationsCount.value = undefined;
        usersCount.value = undefined;
        orgUnit.value = undefined;
        customAttributes.value = undefined;
        contactGroupsDisabled.value = false;

        if (!teamId || !groupId) {
            group.value = {};
            loading.value = false;
            onMountedGroupCallback?.();
            return;
        }

        group.value.group_id = groupId;
        group.value.team_id = teamId;
        loading.value = true;

        const loadedTeam = await getTeam();

        if (!loadedTeam) {
            loading.value = false;
            return;
        }

        if (!loadedTeam.team_attributes?.contact_groups) {
            contactGroupsDisabled.value = true;
            loading.value = false;
            return;
        }

        try {
            const response = await groupsClient.getGroup({ teamId }, groupId);
            group.value = response.data;
            orgUnit.value = response.data.org_unit || undefined;
            loading.value = false;

            if (loadedTeam.owner_id) {
                getTeamOwner();
            }

            onMountedGroupCallback?.();
        } catch (e) {
            catchRequestError(e);
            loading.value = false;
        }
    };

    const unmount = () => {
        group.value = {};
        team.value = undefined;
        teamOwner.value = undefined;
        groupOwner.value = undefined;
        invitationsCount.value = undefined;
        usersCount.value = undefined;
        orgUnit.value = undefined;
        customAttributes.value = undefined;
        contactGroupsDisabled.value = false;
        loading.value = false;
    };

    const refreshGroup = async (teamId: number, groupId: string) => {
        try {
            const response = await groupsClient.getGroup({ teamId }, groupId);
            group.value = response.data;
            orgUnit.value = response.data.org_unit || undefined;
        } catch (e) {
            console.error(e);
        }
    };

    const team = ref<KioskTeam | undefined>(undefined);
    const teamLoading = ref(false);
    const getTeam = async () => {
        if (!group.value.team_id) {
            return;
        }

        if (team.value) {
            return team.value;
        }

        if (teamLoading.value) {
            return;
        }

        try {
            teamLoading.value = true;
            const response = await adminTeamsClient.getTeam(group.value.team_id);
            team.value = response.data;
            return response.data;
        } catch (e) {
            catchRequestError(e);
        } finally {
            teamLoading.value = false;
        }
    };

    const teamOwner = ref<User | undefined>(undefined);
    const teamOwnerLoading = ref(false);
    const getTeamOwner = async () => {
        if (!team.value?.owner_id) {
            return;
        }

        try {
            teamOwnerLoading.value = true;
            const response = await adminUsersClient.showUser(team.value.owner_id);
            teamOwner.value = response.data;
        } catch (e) {
            catchRequestError(e);
        } finally {
            teamOwnerLoading.value = false;
        }
    };

    const groupOwner = ref<{ id: number; name: string; email: string; role: string } | undefined>(undefined);
    const groupOwnerLoading = ref(false);
    const getGroupOwner = async () => {
        if (!group.value.team_id || !group.value.group_id) {
            return;
        }

        try {
            groupOwnerLoading.value = true;
            const response = await groupUsersClient.listGroupUsers(group.value.team_id, group.value.group_id, {
                role: 'owner',
            });
            groupOwner.value = response.data.data[0];
        } catch (e) {
            catchRequestError(e);
        } finally {
            groupOwnerLoading.value = false;
        }
    };

    const invitationsCount = ref<number | undefined>(undefined);
    const invitationsCountLoading = ref(false);
    const getInvitationsCount = async () => {
        try {
            invitationsCountLoading.value = true;
            const response = await groupInvitationsClient.invitationsCount(group.value.team_id, group.value.group_id);
            invitationsCount.value = response.data.count;
        } catch (e) {
            catchRequestError(e);
        } finally {
            invitationsCountLoading.value = false;
        }
    };

    const usersCount = ref<number | undefined>(undefined);
    const usersCountLoading = ref(false);
    const getUsersCount = async () => {
        try {
            usersCountLoading.value = true;
            const response = await groupUsersClient.countGroupUsers(group.value.team_id, group.value.group_id);
            usersCount.value = response.data.count;
        } catch (e) {
            catchRequestError(e);
        } finally {
            usersCountLoading.value = false;
        }
    };

    const orgUnit = ref<OrgUnit | undefined>(undefined);
    const orgUnitLoading = ref(false);

    const customAttributes = ref<{ key: string; value: string }[] | undefined>(undefined);
    const customAttributesLoading = ref(false);
    const getCustomAttributes = async () => {
        try {
            customAttributesLoading.value = true;
            const response = await groupsClient.getGroupCustomAttributes(
                { teamId: group.value.team_id! },
                group.value.group_id!
            );
            customAttributes.value = response.data.data;
        } catch (e) {
            catchRequestError(e);
        } finally {
            customAttributesLoading.value = false;
        }
    };

    return {
        loading,
        contactGroupsDisabled,
        group,
        mount,
        unmount,
        refreshGroup,
        onMountedGroup,

        team,
        teamLoading,
        getTeam,

        teamOwner,
        teamOwnerLoading,
        getTeamOwner,

        groupOwner,
        groupOwnerLoading,
        getGroupOwner,

        invitationsCount,
        invitationsCountLoading,
        getInvitationsCount,

        usersCount,
        usersCountLoading,
        getUsersCount,

        orgUnit,
        orgUnitLoading,

        customAttributes,
        customAttributesLoading,
        getCustomAttributes,
    };
});
